import { useEffect, useState } from "react";
import { useWatch, useFormContext } from "react-hook-form";
import Tooltip from "../../../Tooltip";
import CommentGroup from "../../../ADP/CommentGroup";
import styles from "./index.module.scss";
import PageMarker from "../../../ADP/PageMarker";
import TextArea from "../../../TextArea";
import CheckboxGroup from "../../../CheckboxGroup";
import CheckboxSection from "../../../CheckboxSection";
import { nonSpecialCharacterOptions } from "../../../../utils/forms/validation";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";
import { useStoreContext } from "../../../../contexts/StoreProvider";

//#region options
const equipmentOptions = [
  {
    label: "Forearm crutches",
    value: "prev-forearm-crutches",
  },
  {
    label: "Wheeled walker",
    value: "prev-wheeled-walker",
  },
  {
    label: "Manual wheelchair",
    value: "prev-manual-wheelchair",
  },
  {
    label: "Power wheelchair",
    value: "prev-power-wheelchair",
  },
  {
    label: "Power add on device",
    value: "prev-power-addon",
  },
  {
    label: "Power scooter",
    value: "prev-power-scooter",
  },
  {
    label: "Positioning devices (seating)",
    value: "prev-positioning-devices",
  },
  {
    label: "Power tilt system",
    value: "prev-power-tilt",
  },
  {
    label: "Power recline system",
    value: "prev-power-recline",
  },
  {
    label: "Power elevating leg rests",
    value: "prev-power-elevating",
  },
  {
    label: "Paediatric standing frame",
    value: "prev-paediatric-standing",
  },
  {
    label: "Paediatric specific specialty stroller",
    value: "prev-paediatric-specific",
  },
  {
    label: "None",
    value: "prev-none",
  },
];

const devicesOptions = [
  {
    label: "Forearm crutches only to achieve independent mobility",
    name: "req-forearm-crutches",
    sections: ["info.section2a-show"],
  },
  {
    label: "A wheeled walker only to achieve independent mobility",
    name: "req-wheeled-walker",
    sections: ["info.section2a-show"],
  },
  {
    label: "A manual wheelchair only to achieve independent mobility",
    name: "req-manual-wheelchair-independent",
    sections: ["info.section2b-show"],
  },
  {
    label:
      "An ambulation aid and a manual wheelchair to achieve independent mobility",
    name: "req-ambulation-aid-manual",
    sections: ["info.section2a-show", "info.section2b-show"],
  },
  {
    label: "A manual wheelchair to achieve mobility (dependent for propulsion)",
    name: "req-manual-wheelchair-propulsion",
    sections: ["info.section2b-show"],
  },
  {
    label: "A manual dynamic tilt wheelchair to achieve independent mobility",
    name: "req-dynamic-tilt-independent",
    sections: ["info.section2b-show"],
  },
  {
    label:
      "A manual dynamic tilt wheelchair to achieve mobility (dependent for propulsion)",
    name: "req-dynamic-tilt-propulsion",
    sections: ["info.section2b-show"],
  },
  {
    label:
      "A manual wheelchair with a power add-on device to achieve independent mobility",
    name: "req-manual-wheelchair-power",
    sections: ["info.section2b-show"],
  },
  {
    label: "A power base only to achieve independent mobility",
    name: "req-power-base",
    sections: ["info.section2c-show"],
  },
  {
    label: "A power scooter only to achieve independent mobility",
    name: "req-power-scooter",
    sections: ["info.section2c-show"],
  },
  {
    label:
      "An ambulation aid and a power base/scooter to achieve independent mobility",
    name: "req-ambulation-aid-power",
    sections: ["info.section2a-show", "info.section2c-show"],
  },
  {
    label:
      "Positioning devices (seating) for a wheelchair - modular and/or custom fabricated",
    name: "req-positioning-devices",
    sections: ["info.section2d-show"],
  },
  {
    label:
      "A high technology power base (dynamic tilt and/or recline and/or power elevating leg rests)",
    name: "req-high-tech-power-base",
    sections: ["info.section2c-show"],
  },
  {
    label: "A paediatric standing frame",
    name: "req-paediatric-standing",
    sections: ["info.section2a-show"],
  },
  {
    label: "Modifications to previously ADP funded device(s)",
    name: "req-modifications-ADP",
    sections: [
      "info.section2a-show",
      "info.section2b-show",
      "info.section2c-show",
    ],
    subOptions: [
      {
        label: "Ambulation Aids",
        id: "ambulation-pf",
        value: "ambulation-pf",
        sections: ["info.section2a-show"],
      },
      {
        label: "Manual Wheelchair",
        id: "manual-wheelchair-pf",
        value: "manual-wheelchair-pf",
        sections: ["info.section2b-show"],
      },
      {
        label: "Power Wheelchair",
        id: "power-wheelchair-pf",
        value: "power-wheelchair-pf",
        sections: ["info.section2c-show"],
      },
      {
        label: "Ambulation Aids + Manual Wheelchair",
        id: "ambulation-manual-wheelchair-pf",
        value: "ambulation-manual-wheelchair-pf",
        sections: ["info.section2a-show"],
      },
      {
        label: "Ambulation Aids + Power Wheelchair",
        id: "ambulation-power-wheelchair-pf",
        value: "ambulation-power-wheelchair-pf",
        sections: ["info.section2a-show"],
      },
    ],
  },
  {
    label: "Modifications to non ADP funded device(s)",
    name: "req-modifications-non-ADP",
    sections: [
      "info.section2a-show",
      "info.section2b-show",
      "info.section2c-show",
    ],
    subOptions: [
      {
        label: "Ambulation Aids",
        id: "ambulation-npf",
        value: "ambulation-npf",
        sections: ["info.section2a-show"],
      },
      {
        label: "Manual Wheelchair",
        id: "manual-wheelchair-npf",
        value: "manual-wheelchair-npf",
        sections: ["info.section2b-show"],
      },
      {
        label: "Power Wheelchair",
        id: "power-wheelchair-npf",
        value: "power-wheelchair-npf",
        sections: ["info.section2c-show"],
      },
      {
        label: "Ambulation Aids + Manual Wheelchair",
        id: "ambulation-manual-wheelchair-npf",
        value: "ambulation-manual-wheelchair-npf",
        sections: ["info.section2a-show"],
      },
      {
        label: "Ambulation Aids + Power Wheelchair",
        id: "ambulation-power-wheelchair-npf",
        value: "ambulation-power-wheelchair-npf",
        sections: ["info.section2a-show"],
      },
    ],
  },
];

//#endregion

const Section2 = () => {
  const { state: { externalUrls }, } = useStoreContext();

  const { clientFile, formMode: mode } = useClientFileContext();
  const { setValue } = useFormContext();

  const [oldPrevFund, setOldPrevFund] = useState(
    useWatch({ name: "section2.previous-equipment" })
  );
  const curPrevFund = useWatch({ name: "section2.previous-equipment" });

  // LOGIC for none selection in previous equipment
  useEffect(() => {
    if (oldPrevFund !== curPrevFund) {
      // If none is selected, clear others
      if (
        oldPrevFund &&
        !oldPrevFund.includes("prev-none") &&
        curPrevFund?.length >= 2 &&
        curPrevFund?.includes("prev-none")
      ) {
        setValue(
          "section2.previous-equipment",
          curPrevFund?.filter((x) => ["prev-none"].includes(x))
        );
      }

      // If none was selected and something else is selected, clear none
      if (
        oldPrevFund &&
        oldPrevFund.includes("prev-none") &&
        curPrevFund?.length >= 2
      ) {
        setValue(
          "section2.previous-equipment",
          curPrevFund?.filter((x) => !["prev-none"].includes(x))
        );
      }

      setOldPrevFund(curPrevFund);
    }
  }, [oldPrevFund, setOldPrevFund, curPrevFund, setValue]);

  return (
    <>
      <CommentGroup
        section="section2"
        id="1-medical-condition"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextArea
          section="section2"
          id="medical-condition"
          label="Applicant's presenting medical condition"
          description="This must be completed. Please be as thorough as possible."
          placeholder="Notes on applicant’s presenting medical condition"
          maxLength={330}
          fieldOptions={{
            ...nonSpecialCharacterOptions,
            required: "This is a required field.",
          }}
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="medical-condition-tooltip"
              content="If an extra seat cover or back cover is being prescribed, the client's diagnoses with incontinence needs to be mentioned here."
            />
          }
        />
      </CommentGroup>
      <CommentGroup
        section="section2"
        id="2-mobility-status"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextArea
          section="section2"
          id="mobility-status"
          label="Applicant’s basic functional mobility status related to the need for an ADP funded device"
          description={`This must be completed. Please be as thorough as possible.
        Please describe your clinical assessment results that this is the basic piece of equipment required.`}
          placeholder="Notes on applicant’s presenting medical condition"
          maxLength={330}
          fieldOptions={{
            ...nonSpecialCharacterOptions,
            required: "This is a required field.",
          }}
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="mobility-status-tooltip"
              content="Please describe your clinical assessment results that this is the basic piece of equipment required"
            />
          }
        />
      </CommentGroup>
      <PageMarker page={2} total={13} />
      <CommentGroup
        section="section2"
        id="3-previous-equipment"
        className={styles.marginAfterPM}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section2"
          options={equipmentOptions}
          name="previous-equipment"
          label="Mobility Equipment Previously Funded by ADP (check one or more as appropriate)"
          requiredText=" This is a required field."
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2"
        id="4-devices-required"
        className={styles.margin}
        customPosition={styles.devicesComment}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxSection
          section="section2"
          options={devicesOptions}
          name="devices-required"
          label="Device(s) Currently Required by the Applicant on an ongoing daily basis, Based on Eligibility Criteria for ADP Funding
        Assistance"
          description={`Complete and submit the relevant Section(s) below.
        Check one or more as appropriate - at least one option needs to be selected to proceed.`}
          requiredText="This is a required field."
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="devices-required-tooltip"
              content="If the client has a scooter/power wheelchair and if they are applying for a Type 3 walker (in (Section 2a), ADP will not fund this walker. Please check the ADP Policy and Procedures Manual for further information."
              links={[
                {
                  id: "link-1",
                  text: "Mobility Devices Manual",
                  href: externalUrls.mobility_devices_policy_and_administration_manual,
                },
              ]}
            />
          }
        />
      </CommentGroup>
    </>
  );
};

export default Section2;
